import sanitizeHtml from 'sanitize-html'
import {
  updateCommercialRealEstateType,
  updateEmployment,
  updateEquity,
  updateExtendedBuyerLead,
  updateIncome,
  updateIntent,
  updateLivingSpace,
  updateOptionalHouseNumber,
  updateOptionalStreet,
  updateOwnership,
  updatePlacementType,
  updatePlotArea,
  updateRealEstateType,
  updateRoomsNumber,
  updateTimeHorizon,
} from '../store/lead/leadSlice'
import { updateSuccessLocation } from '../store/location/locationSlice'
import { store } from '../store/store'
import { RealEstateType } from '../@types/realEstateType'
import { Intent } from '../@types/intent'
import { STEP } from '../@types/state'
import { gacApiFetch, getGeoEntity } from '../clients/geoClient'
import { FlowMachineContext } from '../state/flowMachine'
import { Status } from '../@types/status'
import { TimeHorizon } from '../@types/timeHorizon'
import { Ownership } from '../@types/ownership'
import { CommercialRealEstateType } from '../@types/commercialRealEstateType'
import { WizardFlowType } from '../@types/wizardFlowType'
import {
  updateAdCampaign,
  updateAdChannel,
  updateAdDevice,
  updateAdGroup,
  updateAdKeyword,
  updateAdPlacement,
  updateClickId,
  updateKwMatchType,
} from '../store/immoverkauf24/trackingSlice'
import { IntegrationType } from '../@types/config'
import { isEquityWithinRange } from '../steps/equity/EquityStep'
import { isIncomeWithinRange } from '../steps/income/IncomeStep'
import { PlacementType } from '../@types/placementType'
import { validateLivingSpace, validatePlotArea } from '../components/Area/utils'
import { isValidRoomsNumber } from '../components/RoomsStepper/RoomsStepper'

enum QueryParams {
  ROOMS = 'rooms',
  PROPERTY = 'property',
  LIVING_SPACE = 'size',
  AREA = 'area',
  POSTCODE = 'ZIP',
  STREET = 'street',
  HOUSE_NUMBER = 'houseNumber',
  INTENT = 'intent',
  SELL_INTENT = 'realtor',
  TIME_HORIZON = 'selltime',
  OWNERSHIP = 'ownership',
  DEV_COM = 'devcom',
  COMMERCIAL_REAL_ESTATE_TYPE = 'subtype',
  PARTIAL_SALE = 'partialsale',
  AD_GROUP = 'adgroup',
  AD_KEYWORD = 'keyword',
  AD_PLACEMENT = 'placement',
  AD_CAMPAIGN = 'utm_campaign',
  AD_CHANNEL = 'utm_source',
  AD_DEVICE = 'device',
  AD_CLICK_ID = 'gclid',
  KW_MATCH_TYPE = 'matchtype',
  SKIP_BUYER_LEAD_VALUE_PROPOSITION = 'skipBuyerLeadValueProposition',
  EMPLOYMENT = 'employment',
  EQUITY = 'equity',
  INCOME = 'income',
  PLACEMENT_TYPE = 'placementType',
}

enum QueryParamsIntent {
  RENT = 'RENT',
  LET = 'LET',
  BUY = 'BUY',
  SELL = 'SELL',
}

enum QueryParamsWithRealtorIntent {
  YES = 'YES',
  NO = 'NO',
  MAYBE = 'MAYBE',
}

enum QueryParamsDevCom {
  YES = 'YES',
  NO = 'NO',
}

enum QueryParamsPartialSale {
  YES = 'YES',
  NO = 'NO',
}

const queryParamsCommercialSubType = new Map<string, CommercialRealEstateType>([
  ['OFFICE', CommercialRealEstateType.OFFICE],
  ['HALL', CommercialRealEstateType.HALL],
  ['RETAIL', CommercialRealEstateType.RETAIL],
  ['GASTRO', CommercialRealEstateType.GASTRONOMY],
  ['OTHER', CommercialRealEstateType.MISCELLANEOUS],
])

const getQueryParameters = () => location.search.substr(1)

const parseParamValue = (paramName: string) => {
  const searchParams = new URLSearchParams(getQueryParameters())
  const value = searchParams.get(paramName)
  return value ? sanitizeHtml(value) : undefined
}

const getRealEstateType = (): RealEstateType | undefined => {
  const propertyValue = parseParamValue(QueryParams.PROPERTY)
  return propertyValue && propertyValue.toUpperCase() in RealEstateType
    ? RealEstateType[propertyValue.toUpperCase() as RealEstateType]
    : undefined
}

const getParamValue = (paramName: QueryParams): Promise<string> | string | undefined => {
  switch (paramName) {
    case QueryParams.ROOMS:
      return isValidRoomsNumber(Number(parseParamValue(QueryParams.ROOMS)))
        ? parseParamValue(QueryParams.ROOMS)
        : undefined

    case QueryParams.PROPERTY:
      return getRealEstateType()

    case QueryParams.LIVING_SPACE: {
      const realEstateType = getRealEstateType()
      if (realEstateType) {
        return validateLivingSpace(realEstateType, Number(parseParamValue(QueryParams.LIVING_SPACE)))
          ? parseParamValue(QueryParams.LIVING_SPACE)
          : undefined
      }
      break
    }

    case QueryParams.AREA: {
      const realEstateType = getRealEstateType()
      if (realEstateType) {
        return validatePlotArea(realEstateType, Number(parseParamValue(QueryParams.AREA)))
          ? parseParamValue(QueryParams.AREA)
          : undefined
      }
      break
    }

    case QueryParams.POSTCODE: {
      const postcode = parseParamValue(QueryParams.POSTCODE)
      return postcode && postcode.length == 5 && parseInt(postcode) ? parseParamValue(QueryParams.POSTCODE) : undefined
    }

    case QueryParams.STREET: {
      const street = parseParamValue(QueryParams.STREET)
      return street ? parseParamValue(QueryParams.STREET) : undefined
    }

    case QueryParams.SKIP_BUYER_LEAD_VALUE_PROPOSITION: {
      const skipBuyerLeadValueProposition = parseParamValue(QueryParams.SKIP_BUYER_LEAD_VALUE_PROPOSITION)
      return skipBuyerLeadValueProposition ? parseParamValue(QueryParams.SKIP_BUYER_LEAD_VALUE_PROPOSITION) : undefined
    }

    case QueryParams.HOUSE_NUMBER: {
      const houseNumber = parseParamValue(QueryParams.HOUSE_NUMBER)
      return houseNumber ? parseParamValue(QueryParams.HOUSE_NUMBER) : undefined
    }

    case QueryParams.INTENT: {
      const intent = parseParamValue(QueryParams.INTENT)
      return intent && intent.toUpperCase() in QueryParamsIntent
        ? QueryParamsIntent[intent.toUpperCase() as QueryParamsIntent]
        : undefined
    }

    case QueryParams.SELL_INTENT: {
      const sellIntent = parseParamValue(QueryParams.SELL_INTENT)
      return sellIntent && sellIntent.toUpperCase() in QueryParamsWithRealtorIntent
        ? QueryParamsWithRealtorIntent[sellIntent.toUpperCase() as QueryParamsWithRealtorIntent]
        : undefined
    }

    case QueryParams.EMPLOYMENT: {
      const employment = parseParamValue(QueryParams.EMPLOYMENT)
      return employment ? parseParamValue(QueryParams.EMPLOYMENT) : undefined
    }

    case QueryParams.TIME_HORIZON: {
      const saleTimeHorizon = parseParamValue(QueryParams.TIME_HORIZON)
      return saleTimeHorizon && saleTimeHorizon.toUpperCase() in TimeHorizon
        ? TimeHorizon[saleTimeHorizon.toUpperCase() as TimeHorizon]
        : undefined
    }

    case QueryParams.OWNERSHIP: {
      const ownerShip = parseParamValue(QueryParams.OWNERSHIP)
      return ownerShip && ownerShip.toUpperCase() in Ownership
        ? Ownership[ownerShip.toUpperCase() as Ownership]
        : undefined
    }

    case QueryParams.COMMERCIAL_REAL_ESTATE_TYPE: {
      const commercialRealEstateType = parseParamValue(QueryParams.COMMERCIAL_REAL_ESTATE_TYPE)?.toUpperCase()
      return commercialRealEstateType ? queryParamsCommercialSubType.get(commercialRealEstateType) : undefined
    }

    case QueryParams.DEV_COM: {
      const devCom = parseParamValue(QueryParams.DEV_COM)
      return devCom && devCom.toUpperCase() in QueryParamsDevCom
        ? QueryParamsDevCom[devCom.toUpperCase() as QueryParamsDevCom]
        : undefined
    }
    case QueryParams.PARTIAL_SALE: {
      const partialSale = parseParamValue(QueryParams.PARTIAL_SALE)
      return partialSale && partialSale.toUpperCase() in QueryParamsPartialSale
        ? QueryParamsPartialSale[partialSale.toUpperCase() as QueryParamsPartialSale]
        : undefined
    }
    case QueryParams.AD_GROUP:
    case QueryParams.AD_KEYWORD:
    case QueryParams.AD_PLACEMENT:
    case QueryParams.AD_CAMPAIGN:
    case QueryParams.AD_CHANNEL:
    case QueryParams.AD_DEVICE:
    case QueryParams.AD_CLICK_ID:
    case QueryParams.KW_MATCH_TYPE: {
      return parseParamValue(paramName)
    }
    case QueryParams.EQUITY:
    case QueryParams.INCOME: {
      const parsedValue = parseParamValue(paramName)
      const inputValue = Number(parsedValue)
      return isNaN(inputValue) ? undefined : parsedValue
    }
    case QueryParams.PLACEMENT_TYPE: {
      const placementType = parseParamValue(QueryParams.PLACEMENT_TYPE)
      return placementType && placementType.toUpperCase() in PlacementType ? placementType.toUpperCase() : undefined
    }
  }
  return undefined
}

const dispatchUpdateLocation = async (postcode: string) => {
  const geoEntity = await gacApiFetch(postcode)
    .then((geoItems) => (geoItems.length != 0 ? geoItems[0].entity.id : ''))
    .then((entityId) => getGeoEntity(entityId))
    .catch((errorMsg) => console.error(errorMsg))

  if (geoEntity) store.dispatch(updateSuccessLocation(geoEntity))
}

export const updateStoreFromQueryParam = async (
  flowType: WizardFlowType,
  integrationType: IntegrationType
): Promise<void> => {
  const queryParams = new URLSearchParams(getQueryParameters())
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24

  for (const [prop] of queryParams.entries()) {
    const paramValue = getParamValue(prop as QueryParams)

    switch (prop) {
      case QueryParams.ROOMS:
        if (paramValue) store.dispatch(updateRoomsNumber(Number(paramValue)))
        break
      case QueryParams.PROPERTY:
        if (paramValue) store.dispatch(updateRealEstateType(paramValue as RealEstateType))
        break
      case QueryParams.LIVING_SPACE:
        if (paramValue) store.dispatch(updateLivingSpace(Number(paramValue)))
        break
      case QueryParams.AREA:
        if (paramValue) store.dispatch(updatePlotArea(Number(paramValue)))
        break
      case QueryParams.STREET:
        if (paramValue) store.dispatch(updateOptionalStreet(paramValue as string))
        break
      case QueryParams.HOUSE_NUMBER:
        if (paramValue) store.dispatch(updateOptionalHouseNumber(paramValue as string))
        break
      case QueryParams.POSTCODE:
        if (paramValue) await dispatchUpdateLocation(paramValue as string)
        break
      case QueryParams.TIME_HORIZON: {
        if (paramValue) store.dispatch(updateTimeHorizon(paramValue as TimeHorizon))
        break
      }
      case QueryParams.SKIP_BUYER_LEAD_VALUE_PROPOSITION: {
        if (paramValue) store.dispatch(updateExtendedBuyerLead(paramValue as unknown as boolean))
        break
      }
      case QueryParams.EMPLOYMENT: {
        if (paramValue) store.dispatch(updateEmployment(paramValue as string))
        break
      }
      case QueryParams.OWNERSHIP: {
        if (paramValue) store.dispatch(updateOwnership(paramValue as Ownership))
        break
      }
      case QueryParams.COMMERCIAL_REAL_ESTATE_TYPE: {
        if (paramValue) store.dispatch(updateCommercialRealEstateType(paramValue as CommercialRealEstateType))
        break
      }
      case QueryParams.SELL_INTENT: {
        const realtorIntent = getParamValue(QueryParams.SELL_INTENT)
        handleSellIntent(realtorIntent, WizardFlowType.IBW)
        break
      }
      case QueryParams.DEV_COM: {
        const devCom = getParamValue(QueryParams.DEV_COM)
        handleDevCom(devCom)
        break
      }
      case QueryParams.INTENT: {
        if (paramValue && paramValue !== QueryParamsIntent.SELL) store.dispatch(updateIntent(paramValue as Intent))
        else if (paramValue === QueryParamsIntent.SELL) {
          const userIntent = getParamValue(QueryParams.INTENT)
          handleUserIntent(userIntent)
          const realtorIntent = getParamValue(QueryParams.SELL_INTENT)
          const devCom = getParamValue(QueryParams.DEV_COM)
          handleSellIntent(realtorIntent, flowType)
          handleDevCom(devCom)
        }
        break
      }
      case QueryParams.PARTIAL_SALE: {
        if (paramValue === QueryParamsPartialSale.YES) {
          store.dispatch(updateIntent(Intent.PARTIAL_SALE))
          break
        }
        store.dispatch(updateIntent(Intent.INDEPENDENT_SALE))
        break
      }
      case QueryParams.AD_GROUP: {
        if (paramValue && isIv24Integration) store.dispatch(updateAdGroup(paramValue as string))
        break
      }
      case QueryParams.AD_KEYWORD: {
        if (paramValue && isIv24Integration) store.dispatch(updateAdKeyword(paramValue as string))
        break
      }
      case QueryParams.AD_PLACEMENT: {
        if (paramValue && isIv24Integration) store.dispatch(updateAdPlacement(paramValue as string))
        break
      }
      case QueryParams.AD_CAMPAIGN: {
        if (paramValue && isIv24Integration) store.dispatch(updateAdCampaign(paramValue as string))
        break
      }
      case QueryParams.AD_CHANNEL: {
        if (paramValue && isIv24Integration) store.dispatch(updateAdChannel(paramValue as string))
        break
      }
      case QueryParams.AD_DEVICE: {
        if (paramValue && isIv24Integration) store.dispatch(updateAdDevice(paramValue as string))
        break
      }
      case QueryParams.AD_CLICK_ID: {
        if (paramValue && isIv24Integration) store.dispatch(updateClickId(paramValue as string))
        break
      }
      case QueryParams.KW_MATCH_TYPE: {
        if (paramValue && isIv24Integration) store.dispatch(updateKwMatchType(paramValue as string))
        break
      }
      case QueryParams.EQUITY: {
        const equity = Number(paramValue)
        if (paramValue !== '' && isEquityWithinRange(equity)) store.dispatch(updateEquity(equity))
        break
      }
      case QueryParams.INCOME: {
        const income = Number(paramValue)
        if (paramValue !== '' && isIncomeWithinRange(income)) store.dispatch(updateIncome(income))
        break
      }
      case QueryParams.PLACEMENT_TYPE: {
        if (paramValue) store.dispatch(updatePlacementType(paramValue as PlacementType))
        break
      }
    }
  }
}

export const getSkippedStepsByQueryParams = (ctx: FlowMachineContext): STEP[] => {
  const queryParams = new URLSearchParams(getQueryParameters())
  const skippedSteps: STEP[] = []

  const { lead, location } = ctx

  for (const [prop] of queryParams.entries()) {
    switch (prop) {
      case QueryParams.EMPLOYMENT:
        if (lead && lead.employment) skippedSteps.push(STEP.EMPLOYMENT)
        break
      case QueryParams.SKIP_BUYER_LEAD_VALUE_PROPOSITION:
        skippedSteps.push(STEP.BUYER_LEADS_VALUE_PROPOSITION)
        break
      case QueryParams.ROOMS:
        if (lead && lead.rooms) skippedSteps.push(STEP.ROOMS)
        break
      case QueryParams.PROPERTY:
        if (lead && lead.realEstateType) skippedSteps.push(STEP.REAL_ESTATE_TYPE)
        break
      case QueryParams.PLACEMENT_TYPE:
        if (lead && lead.placementType) skippedSteps.push(STEP.PLACEMENT_TYPE)
        break
      case QueryParams.INTENT: {
        if (lead && lead.intent) skippedSteps.push(STEP.INTENT)
        break
      }
      case QueryParams.POSTCODE:
        if (location && location.status === Status.SUCCESS) skippedSteps.push(STEP.LOCATION)
        break
      case QueryParams.TIME_HORIZON:
        if (lead && lead.timeHorizon) skippedSteps.push(STEP.TIME_HORIZON)
        break
      case QueryParams.OWNERSHIP:
        if (lead && lead.ownership) skippedSteps.push(STEP.OWNER)
        break
      case QueryParams.COMMERCIAL_REAL_ESTATE_TYPE:
        if (lead && lead.commercialRealEstateType) skippedSteps.push(STEP.COMMERCIAL_REAL_ESTATE_TYPE)
        break
      case QueryParams.SELL_INTENT:
        if (lead && lead.intent) {
          skippedSteps.push(STEP.SELL_INTENT, STEP.REINFORCEMENT)
        }
        break
      case QueryParams.DEV_COM:
        if (lead && lead.intent) {
          switch (lead.intent) {
            case Intent.INDEPENDENT_SALE:
              skippedSteps.push(STEP.DEV_COM_OFFER)
              break
            case Intent.INDEPENDENT_WITH_OFFER_SALE:
              skippedSteps.push(STEP.DEV_COM_OFFER)
              break
          }
        }
        break
      case QueryParams.PARTIAL_SALE:
        if (lead && lead.intent) {
          skippedSteps.push(STEP.PARTIAL_SALE)
        }
        break
      case QueryParams.EQUITY:
        if (lead && lead.equity) {
          skippedSteps.push(STEP.EQUITY)
        }
        break
      case QueryParams.INCOME:
        if (lead && lead.income) {
          skippedSteps.push(STEP.INCOME)
        }
        break
    }
  }

  if (skippedSteps.includes(STEP.REAL_ESTATE_TYPE)) {
    const realEstateType = lead ? lead.realEstateType : undefined
    const livingSpace = lead ? lead.livingSpace : undefined
    const plotArea = lead ? lead.plotArea : undefined
    if (
      (realEstateType === RealEstateType.APARTMENT && livingSpace) ||
      (realEstateType === RealEstateType.LAND && plotArea) ||
      (realEstateType === RealEstateType.HOUSE && livingSpace && plotArea) ||
      (realEstateType === RealEstateType.COMMERCIAL && livingSpace && plotArea)
    ) {
      skippedSteps.push(STEP.AREA)
    }
  }
  return skippedSteps
}

const handleDevCom = (devCom: string | undefined | Promise<string>) => {
  switch (devCom as QueryParamsDevCom) {
    case QueryParamsDevCom.YES:
      store.dispatch(updateIntent(Intent.INDEPENDENT_WITH_OFFER_SALE))
      break
    case QueryParamsDevCom.NO:
      store.dispatch(updateIntent(Intent.INDEPENDENT_SALE))
  }
}

const handleUserIntent = (userIntent: string | undefined | Promise<string>) => {
  switch (userIntent as QueryParamsIntent) {
    case QueryParamsIntent.SELL:
      store.dispatch(updateIntent(Intent.MAKLER_SALE))
      break
  }
}

const handleSellIntent = (realtorIntent: string | undefined | Promise<string>, flowType: WizardFlowType) => {
  switch (realtorIntent as QueryParamsWithRealtorIntent) {
    case QueryParamsWithRealtorIntent.YES:
      store.dispatch(updateIntent(Intent.MAKLER_SALE))
      break
    case QueryParamsWithRealtorIntent.NO:
      store.dispatch(updateIntent(Intent.INDEPENDENT_SALE))
      break
    case QueryParamsWithRealtorIntent.MAYBE:
      if (flowType == WizardFlowType.IBW) store.dispatch(updateIntent(Intent.MAYBE_MAKLER_SALE))
      break
  }
}
